import React from 'react'
import ReactDOM from 'react-dom'
import { observer, Provider, inject } from 'mobx-react'
import { StateModel, StateType } from '../state/state'
import { ScatterplotModel } from '../state/ScatterplotState'
import { ControlPanel } from './ControlPanel'
import { Scatterplot } from './Scatterplot'
import { useDevice } from '../../../lib/hooks/useDevice'
import { useParentSize } from '../../../lib/hooks/useParentSize'
import { ReactComponent as FiltersIcon } from '../../../assets/filters.svg'
import { Modal } from './Modal'
import { ChallengesButtons } from './ChallengesButtons'
import { YearsSlider } from './YearsSlider'
import { maxAspectRatio } from '../../../lib/math-utils'
import { clamp } from 'lodash-es'

const DESKTOP_THRESHOLD = 960

interface Props {
  className?: string
}

@observer
export class ScatterplotContainer extends React.Component<Props> {
  state = StateModel.create({
    ScatterplotState: ScatterplotModel.create({}),
  })

  render() {
    return (
      <Provider state={this.state}>
        <Layout />
      </Provider>
    )
  }
}

export function mountScatterplot(node: HTMLElement) {
  ReactDOM.render(<ScatterplotContainer />, node)
}

export const Layout = () => {
  const [isMobile] = useDevice(DESKTOP_THRESHOLD)
  return isMobile ? <MobileLayout /> : <DesktopLayout />
}

interface DesktopLayoutProps {
  className?: string
  state?: StateType
}
const DesktopLayout: React.FC<DesktopLayoutProps> = inject('state')(
  observer(({ className = '', state }) => {
    const { debug } = state.ScatterplotState

    return (
      <div className={`-desktop-layout center flex ${className}`} style={{ maxWidth: 1312 }}>
        <div className={`w-30 min-w5 ${debug ? 'ba' : ''}`}>
          <ControlPanel />
        </div>
        <div className={`flex-grow-1 ${debug ? 'ba' : ''}`}>
          <Scatter />
        </div>
      </div>
    )
  })
)

interface MobileLayoutProps {
  className?: string
  state?: StateType
}
const MobileLayout: React.FC<MobileLayoutProps> = inject('state')(
  observer(({ className = '', state }) => {
    const { setIsMobileModalOpened, debug } = state.ScatterplotState

    return (
      <div className={`-mobile-layout remove-prod-margins ${className}`}>
        <Modal />

        <div className={`flex flex-column h-100 ${debug ? 'ba' : ''}`}>
          <div className={`-controls ${debug ? 'ba' : ''}`}>
            <div
              className={`-control-container add-prod-padding flex w-100 justify-between f6 fw6 pv2 ${
                debug ? 'ba' : ''
              }`}
            >
              <div className="gray700 fw5">EMISSIONS BY CHALLENGE</div>
              <div
                className="flex flex-center blue500 pointer"
                onClick={() => setIsMobileModalOpened(true)}
              >
                <div className="mr2 nowrap">MORE FILTERS</div>
                <FiltersIcon className="w1" />
              </div>
            </div>
            <div className={`${debug ? 'ba' : ''}`}>
              <ChallengesButtons isHorizontal />
            </div>
          </div>

          <Scatter className={`flex-grow-1 ${debug ? 'ba' : ''}`} />
          <div className="center w-80">
            <YearsSlider className={`pt4 pb3 ${debug ? 'ba' : ''}`} />
          </div>
        </div>
      </div>
    )
  })
)

interface ScatterProps {
  className?: string
}
export const Scatter: React.FC<ScatterProps> = ({ className = '' }) => {
  const [containerRef, { width }] = useParentSize<HTMLDivElement>()
  const proportion = 1.25 // width/height = 1.25

  const DESKTOP_HEADER_HEIGHT = 150 // production header (a little larger to leave space)
  const MOBI_HEADER_HEIGHT = 52 // production header
  const MOBI_VIZ_HEADER_AND_FOOTER = 72 + 72 // our FilterPanel and YearsSlider

  const height =
    window.innerWidth < DESKTOP_THRESHOLD
      ? clamp(window.innerHeight - MOBI_VIZ_HEADER_AND_FOOTER - MOBI_HEADER_HEIGHT, 350, 500)
      : clamp(window.innerHeight - DESKTOP_HEADER_HEIGHT, 400, 600)

  const size = maxAspectRatio({ width, height }, proportion)

  return (
    <div className={`w-100 h-100 flex justify-center ${className}`} ref={containerRef}>
      <Scatterplot width={size.width} height={size.height} />
    </div>
  )
}
